import { supabase } from '@/lib/supabase/client';
import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';
import type { SummaryVariant } from '@/types/home-summary';
import type { CalendarEvent } from '@/types/events';

/**
 * CalendarSyncService handles calendar synchronization specifically for summary generation.
 * This service runs client-side and syncs device calendars to Supabase before triggering AI summaries.
 */
type SyncResult = { success: boolean; syncedCount: number; error?: string };

export class CalendarSyncService {
  private static instance: CalendarSyncService | null = null;
  private inFlight: Promise<SyncResult> | null = null;
  private inFlightKey: string | null = null;

  /**
   * Get singleton instance
   */
  static getInstance(): CalendarSyncService {
    if (!CalendarSyncService.instance) {
      CalendarSyncService.instance = new CalendarSyncService();
    }
    return CalendarSyncService.instance;
  }

  /**
   * Helper to create ISO timestamp from date and time strings
   */
  private createTimestamp(dateStr: string, timeStr?: string | null): string {
    if (!timeStr) {
      return `${dateStr}T00:00:00.000Z`;
    }
    return `${dateStr}T${timeStr}:00.000Z`;
  }

  /**
   * Syncs a single device calendar event to Supabase using client-side auth
   */
  private async syncEvent(userId: string, deviceEvent: CalendarEvent): Promise<boolean> {
    try {
      const eventData = {
        user_id: userId,
        device_event_id: deviceEvent.id,
        device_calendar_id: deviceEvent.deviceCalendarId,
        title: deviceEvent.title,
        start_date: this.createTimestamp(deviceEvent.startDate, deviceEvent.startTime),
        end_date: this.createTimestamp(
          deviceEvent.endDate ?? deviceEvent.startDate,
          deviceEvent.endTime ?? deviceEvent.startTime,
        ),
        all_day: !deviceEvent.startTime,
        description: deviceEvent.description ?? null,
        location: deviceEvent.location ?? null,
        attendees: deviceEvent.attendees ?? [],
        visibility: deviceEvent.visibility ?? 'default',
        transparency: deviceEvent.transparency ?? 'opaque',
        recurrence_rule: null,
        extended_properties: {},
        last_synced_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('calendar_events_sync')
        .upsert(eventData, {
          onConflict: 'user_id,device_event_id',
        });

      if (error) {
        console.error(`[CalendarSyncService] Failed to sync event ${deviceEvent.id}:`, error);
        return false;
      }

      return true;
    } catch (error) {
      console.error(`[CalendarSyncService] Exception syncing event ${deviceEvent.id}:`, error);
      return false;
    }
  }

  /**
   * Syncs calendars for the date range needed by a summary variant.
   *
   * @param userId - User ID for syncing events
   * @param variant - Summary variant (determines date range)
   * @param targetDate - Target date for summary (YYYY-MM-DD)
   * @param calendarIds - Optional array of calendar IDs to sync (defaults to all)
   * @returns Object with sync results and any errors
   */
  async syncForSummary(
    userId: string,
    variant: SummaryVariant,
    targetDate: string,
    calendarIds?: string[]
  ): Promise<SyncResult> {
    // Dedupe concurrent calls: callers asking for the same sync share the same
    // in-flight promise instead of failing the second one. The home screen
    // routinely triggers two summary loads in quick succession (mount + variant
    // change), and both legitimately want the same result.
    const key = `${userId}|${variant}|${targetDate}|${(calendarIds ?? []).slice().sort().join(',')}`;
    if (this.inFlight && this.inFlightKey === key) {
      console.log('[CalendarSyncService] Reusing in-flight sync for', key);
      return this.inFlight;
    }
    if (this.inFlight) {
      console.log('[CalendarSyncService] Awaiting different in-flight sync before starting new one');
      await this.inFlight.catch(() => {});
    }

    this.inFlightKey = key;
    this.inFlight = this.runSync(userId, variant, targetDate, calendarIds).finally(() => {
      this.inFlight = null;
      this.inFlightKey = null;
    });
    return this.inFlight;
  }

  private async runSync(
    userId: string,
    variant: SummaryVariant,
    targetDate: string,
    calendarIds?: string[]
  ): Promise<SyncResult> {
    try {
      // Check calendar permissions
      const { status } = await deviceCalendarService.checkPermissions();
      if (status !== 'granted') {
        console.error('[CalendarSyncService] Calendar permissions not granted');
        return { success: false, syncedCount: 0, error: 'Calendar permissions not granted' };
      }

      // Determine date range based on variant
      const { startDate, endDate } = this.getDateRangeForVariant(variant, targetDate);
      console.log(`[CalendarSyncService] Syncing ${variant} for ${targetDate}: ${startDate.toISOString()} to ${endDate.toISOString()}`);

      // Get calendar IDs if not provided
      let calendarsToSync = calendarIds;
      if (!calendarsToSync || calendarsToSync.length === 0) {
        const deviceCalendars = await deviceCalendarService.getDeviceCalendars();
        calendarsToSync = deviceCalendars.map(cal => cal.id);
        console.log(`[CalendarSyncService] Found ${calendarsToSync.length} device calendars to sync`);
      }

      if (calendarsToSync.length === 0) {
        console.warn('[CalendarSyncService] No calendars found to sync');
        return { success: false, syncedCount: 0, error: 'No calendars found' };
      }

      // Fetch device events for date range
      const deviceEvents = await deviceCalendarService.listEvents(
        calendarsToSync,
        startDate,
        endDate
      );
      console.log(`[CalendarSyncService] Fetched ${deviceEvents.length} device events`);

      // Sync events to Supabase (using client-side Supabase auth)
      let syncedCount = 0;
      for (const event of deviceEvents) {
        const synced = await this.syncEvent(userId, event);
        if (synced) {
          syncedCount++;
        }
      }

      console.log(`[CalendarSyncService] Successfully synced ${syncedCount}/${deviceEvents.length} events to Supabase`);

      return { success: true, syncedCount };
    } catch (error) {
      console.error('[CalendarSyncService] Sync failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, syncedCount: 0, error: errorMessage };
    }
  }

  /**
   * Determines the date range to sync based on summary variant.
   *
   * Morning brief: Today only
   * Evening recap: Today + Tomorrow
   */
  private getDateRangeForVariant(
    variant: SummaryVariant,
    targetDate: string
  ): { startDate: Date; endDate: Date } {
    const start = new Date(`${targetDate}T00:00:00`);
    const end = new Date(`${targetDate}T23:59:59`);

    if (variant === 'evening-recap') {
      // Include tomorrow for evening recap
      end.setDate(end.getDate() + 1);
    }

    return { startDate: start, endDate: end };
  }

  /**
   * Checks if a sync is currently in progress
   */
  isSyncInProgress(): boolean {
    return this.inFlight !== null;
  }
}

// Export singleton instance
export const calendarSyncService = CalendarSyncService.getInstance();
